import * as Yup from "yup"
import {
	einRegex,
	usCountryCodeRegex,
	intlCountryCodeRegex,
	usPhoneNumberRegex,
	intlPhoneNumberRegex,
	flexibleUrlRegex,
} from "../../../../utilities/validation"
import {CA_COUNTRY_CODE, caPostalCode, US_COUNTRY_CODE, usPostalCode} from "../../../../resources/address"
import {
	BusinessAnnualRevenue,
	BusinessNumberOfEmployees,
	BusinessVertical,
	CashFlow,
} from "../../../../resources/application"
import {address, ein, phone, website, name} from "../../../../utilities/validation"

const validationErrorMessages = {
	name: name,
	address: address,
	phone: phone,
	website: website,
	dba: {invalid: "DBA must be 128 characters or less"},
	ein: {
		...ein,
		required: "Please enter the EIN",
	},
	businessVertical: {invalid: "Invalid business vertical selected", required: "Please select a business vertical"},
	yearOfIncorporation: {
		invalid: "Invalid year of incorporation selected",
		required: "Please select a year of incorporation",
	},
	numberOfEmployees: {invalid: "Invalid number of employees selected"},
	annualRevenue: {invalid: "Invalid annual revenue selected"},
	stockSymbol: {required: "Please enter the stock symbol"},
	cashFlow: {invalid: "Invalid cash flow selected"},
}

export default {
	name: Yup.string().required(validationErrorMessages.name.required),
	address: Yup.object({
		street: Yup.string().required(validationErrorMessages.address.street.required),
		city: Yup.string().required(validationErrorMessages.address.city.required),
		state: Yup.string().when("country", {
			is: (country: string) => [US_COUNTRY_CODE, CA_COUNTRY_CODE].includes(country),
			then: Yup.string().required(validationErrorMessages.address.state.required),
		}),
		postalCode: Yup.string()
			.required(validationErrorMessages.address.postalCode.required)
			.when("country", {
				is: US_COUNTRY_CODE,
				then: Yup.string().matches(usPostalCode, validationErrorMessages.address.postalCode.usInvalid),
			})
			.when("country", {
				is: CA_COUNTRY_CODE,
				then: Yup.string().matches(caPostalCode, validationErrorMessages.address.postalCode.caInvalid),
			}),
		country: Yup.string().required(validationErrorMessages.address.country.required),
	}),
	dba: Yup.string().max(128, validationErrorMessages.dba.invalid),
	ein: Yup.string()
		.matches(einRegex, validationErrorMessages.ein.invalid)
		.required(validationErrorMessages.ein.required),
	phone: Yup.object({
		countryCode: Yup.string()
			.required(validationErrorMessages.phone.countryCode.required)
			.matches(
				new RegExp(`${usCountryCodeRegex.source}|${intlCountryCodeRegex.source}`),
				validationErrorMessages.phone.countryCode.required
			),
		number: Yup.string()
			.required(validationErrorMessages.phone.number.required)
			.when("countryCode", {
				is: (value: string) => usCountryCodeRegex.test(value),
				then: Yup.string().matches(usPhoneNumberRegex, validationErrorMessages.phone.number.usInvalid),
			})
			.when("countryCode", {
				is: (value: string) => intlCountryCodeRegex.test(value),
				then: Yup.string().matches(intlPhoneNumberRegex, validationErrorMessages.phone.number.intlInvalid),
			}),
	}),
	website: Yup.string().when("hasNoWebsite", {
		is: (value: boolean) => !value,
		then: Yup.string()
			.matches(flexibleUrlRegex, validationErrorMessages.website.invalid)
			.required(validationErrorMessages.website.required),
	}),
	businessVertical: Yup.string()
		.oneOf(Object.keys(BusinessVertical), validationErrorMessages.businessVertical.invalid)
		.required(validationErrorMessages.businessVertical.required),
	yearOfIncorporation: Yup.string()
		.matches(/^\d{4}$/, validationErrorMessages.yearOfIncorporation.invalid)
		.required(validationErrorMessages.yearOfIncorporation.required),
	numberOfEmployees: Yup.string().oneOf(
		Object.keys(BusinessNumberOfEmployees),
		validationErrorMessages.numberOfEmployees.invalid
	),
	annualRevenue: Yup.string().oneOf(Object.keys(BusinessAnnualRevenue), validationErrorMessages.annualRevenue.invalid),
	stockSymbol: Yup.string(),
	cashFlow: Yup.string().oneOf(Object.keys(CashFlow), validationErrorMessages.cashFlow.invalid),
}
